import React, { useState, useEffect, useContext, useRef, Fragment } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';

import { usePageContentId } from '@confluence/page-context';

import { PublishModal, DiscardModal } from './PublishModal/index.ts';
import { PublishModalContext } from './PublishModalContext.tsx';
import { ShareAndRestrictDialogInPublishModal } from './ShareAndRestrictDialogInPublishModal.tsx';
import type { AutoFocusTargetOptions } from './types.ts';

export type PublishModalDialogsProps = {
	isUnpublishedDraft: boolean;
	contentType: string;
	title: string | null;
	publishButtonDisabled: boolean | undefined;
	userViewingPreview?: boolean;
	initialFocusTarget: AutoFocusTargetOptions;
	shouldShowWorldIcon: boolean;
	shouldShowFooterButtons?: boolean;
	onPublishModalComplete: () => void;
	onHoverPublish?: () => void;
};

export const PublishModalDialogs = ({
	isUnpublishedDraft,
	contentType,
	title,
	publishButtonDisabled,
	userViewingPreview,
	initialFocusTarget,
	shouldShowWorldIcon,
	shouldShowFooterButtons = true,
	onPublishModalComplete,
	onHoverPublish,
}: PublishModalDialogsProps): React.JSX.Element => {
	const [contentId = ''] = usePageContentId();
	const { discardCurrentState, hasModifiedSettings, refetchAll } = useContext(PublishModalContext);
	const [publishModalIsOpen, setPublishModalIsOpen] = useState<boolean>(true);
	const [discardModalIsOpen, setDiscardModalIsOpen] = useState<boolean>(false);
	// A ref is needed here so queuedFinalClose can have its value update right away.
	// It is not used in any render functions, so it's OK to use a ref here.
	const queuedFinalCloseRef = useRef<boolean>(false);
	const [focusField, setFocusField] = useState<AutoFocusTargetOptions>(initialFocusTarget);

	const [permissionsDialogIsOpen, setPermissionsDialogIsOpen] = useState(false);

	useEffect(() => {
		// We need to refetch the PublishModal state immediately before opening the modal so we can be sure we have the latest settings.
		refetchAll();
	}, [refetchAll]);

	const openPermissionsDialog = () => {
		setPermissionsDialogIsOpen(true);
	};
	const closePermissionsDialog = () => {
		setPermissionsDialogIsOpen(false);
	};

	/*
	 * The openModal function below opens the publish modal.
	 * It is sometimes used to return to the publish modal after its sub-dialogs (like the permissions dialog) are closed.
	 */
	const openModal = (focus?: AutoFocusTargetOptions) => {
		setPublishModalIsOpen(true);
		setFocusField(focus);
	};
	// The boolean endPublishExperience should be true if this closes all sub-modals and ends the PublishModal experience.
	// If endPublishExperience is false, the PublishModal will be closed but the PublishModal experience will continue, so other sub-modals like the MovePageDialog can render.
	const closeModal = ({ endPublishExperience }: { endPublishExperience: boolean }) => {
		if (endPublishExperience) {
			queuedFinalCloseRef.current = true;
		}
		setPublishModalIsOpen(false);
	};

	const onCloseComplete = () => {
		if (queuedFinalCloseRef.current) {
			onPublishModalComplete();
		}
	};

	const requestCloseModal = () => {
		if (hasModifiedSettings) {
			setDiscardModalIsOpen(true);
		} else {
			discardCurrentState();
			closeModal({ endPublishExperience: true });
		}
	};

	const closeDiscardModal = () => {
		setDiscardModalIsOpen(false);
	};

	return (
		<Fragment>
			<ModalTransition>
				{publishModalIsOpen && (
					<PublishModal
						publishButtonDisabled={publishButtonDisabled}
						closeModal={closeModal}
						openModal={openModal}
						requestCloseModal={requestCloseModal}
						onCloseComplete={onCloseComplete}
						isUnpublishedDraft={isUnpublishedDraft}
						openPermissionsDialog={() => {
							closeModal({ endPublishExperience: false });
							openPermissionsDialog();
						}}
						userViewingPreview={userViewingPreview}
						title={title}
						focusField={focusField}
						setFocusField={setFocusField}
						shouldShowWorldIcon={shouldShowWorldIcon}
						shouldShowFooterButtons={shouldShowFooterButtons}
						onHoverPublish={onHoverPublish}
					/>
				)}
			</ModalTransition>
			{permissionsDialogIsOpen && ( // ShareAndRestrictDialogController must be rendered outside of a ModalTransition, as it is not a Modal directly.
				<ShareAndRestrictDialogInPublishModal
					contentId={contentId}
					contentType={contentType as 'page' | 'blogpost'}
					onClose={() => {
						closePermissionsDialog();
						openModal('access');
					}}
					isUnpublishedDraft={isUnpublishedDraft}
				/>
			)}
			<ModalTransition>
				{discardModalIsOpen && (
					<DiscardModal
						closeModal={closeModal}
						closeDiscardModal={closeDiscardModal}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				)}
			</ModalTransition>
		</Fragment>
	);
};
